import {
  collection,
  getDocs,
  limit,
  orderBy,
  query,
  where,
} from "firebase/firestore";
import { db } from "../../firebaseConfig";

const PROJECT_COLLECTION = "godown_projects";
const USERS_COLLECTION = "nafed_users";
const BRANCH_COLLECTION = "nafed_state_branches";
const PMC_COLLECTION = "pmc_agencies";
const ALLOCATION_COLLECTION = "pmc_district_allocations";
const MAIL_COLLECTION = "nafed_mail_notifications";

export const NAFED_STATUS_GROUPS = {
  APPLIED: ["APPLIED", "NEW", "SUBMITTED", "PENDING"],
  FIELD_VISIT: ["FIELD_VISIT", "SITE_VISIT", "VISIT_PENDING"],
  DPR: ["DPR", "DPR_PENDING", "DPR_APPROVED", "ESTIMATE", "DESIGN"],
  TENDER: ["TENDER", "TENDERING", "TENDER_PUBLISHED", "TENDER_APPROVAL"],
  LOA: ["LOA", "LOA_ISSUED", "WORK_ORDER"],
  CONSTRUCTION: ["CONSTRUCTION", "UNDER_CONSTRUCTION", "WORK_STARTED"],
  COMPLETED: ["COMPLETED", "HANDOVER", "CLOSED"],
  REJECTED: ["REJECTED", "CANCELLED"],
};

function safeNumber(value) {
  const n = Number(value || 0);
  return Number.isFinite(n) ? n : 0;
}

function getProjectState(p) {
  return p.state || p.stateName || p.projectState || p.pacsState || "";
}

function getProjectDistrict(p) {
  return p.district || p.districtName || p.projectDistrict || p.pacsDistrict || "";
}

function getProjectCapacity(p) {
  return (
    safeNumber(p.capacityMt) ||
    safeNumber(p.capacityMT) ||
    safeNumber(p.capacity) ||
    safeNumber(p.godownCapacity) ||
    safeNumber(p.storageCapacity) ||
    0
  );
}

function getProjectCost(p) {
  return (
    safeNumber(p.projectCost) ||
    safeNumber(p.estimatedCost) ||
    safeNumber(p.estimateAmount) ||
    safeNumber(p.tenderAmount) ||
    safeNumber(p.loaAmount) ||
    safeNumber(p.l1Amount) ||
    0
  );
}

function getPacsKey(p) {
  return (
    p.pacsId ||
    p.societyId ||
    p.societyName ||
    p.pacsName ||
    p.applicantName ||
    p.organizationName ||
    ""
  );
}

function normalizeStatus(status) {
  const s = String(status || "").toUpperCase().replace(/\s+/g, "_");

  for (const [group, values] of Object.entries(NAFED_STATUS_GROUPS)) {
    if (values.some((v) => s.includes(v))) {
      return group;
    }
  }

  return "APPLIED";
}

function isNafedProject(p) {
  return (
    p.nafedProject === true ||
    p.projectSource === "NAFED" ||
    p.applicationPortal === "NAFED_PACS_APPLY"
  );
}

function applyScope(projects, scope = {}) {
  let list = [...projects];

  if (scope.onlyNafed !== false) {
    list = list.filter(isNafedProject);
  }

  if (scope.state) {
    list = list.filter(
      (p) => String(getProjectState(p)).toLowerCase() === String(scope.state).toLowerCase()
    );
  }

  if (Array.isArray(scope.districts) && scope.districts.length) {
    const allowed = scope.districts.map((d) => String(d).toLowerCase());
    list = list.filter((p) => allowed.includes(String(getProjectDistrict(p)).toLowerCase()));
  }

  if (scope.pmcEmail) {
    list = list.filter((p) => {
      const projectPmcEmail =
        p.pmcEmail ||
        p.allocatedPmcEmail ||
        p.assignedPmcEmail ||
        "";
      return (
        !projectPmcEmail ||
        String(projectPmcEmail).toLowerCase() === String(scope.pmcEmail).toLowerCase()
      );
    });
  }

  return list;
}

function buildStateOverview(projects) {
  const map = {};

  projects.forEach((p) => {
    const state = getProjectState(p) || "Unknown";

    if (!map[state]) {
      map[state] = {
        state,
        projects: 0,
        pacsSet: new Set(),
        capacityMt: 0,
        investment: 0,
      };
    }

    map[state].projects += 1;

    const pacsKey = getPacsKey(p);
    if (pacsKey) map[state].pacsSet.add(pacsKey);

    map[state].capacityMt += getProjectCapacity(p);
    map[state].investment += getProjectCost(p);
  });

  return Object.values(map)
    .filter((x) => x.state !== "Unknown")
    .map((x) => ({
      state: x.state,
      projects: x.projects,
      pacs: x.pacsSet.size,
      capacityMt: x.capacityMt,
      investment: x.investment,
    }))
    .sort((a, b) => a.state.localeCompare(b.state));
}

function buildStatusOverview(projects) {
  const base = {
    APPLIED: 0,
    FIELD_VISIT: 0,
    DPR: 0,
    TENDER: 0,
    LOA: 0,
    CONSTRUCTION: 0,
    COMPLETED: 0,
    REJECTED: 0,
  };

  projects.forEach((p) => {
    const key = normalizeStatus(p.status || p.projectStatus || p.workflowStatus);
    base[key] = (base[key] || 0) + 1;
  });

  return base;
}

function buildCapacityOverview(projects) {
  const overview = {
    planning: 0,
    underConstruction: 0,
    completed: 0,
    operational: 0,
    total: 0,
  };

  projects.forEach((p) => {
    const cap = getProjectCapacity(p);
    const status = normalizeStatus(p.status || p.projectStatus || p.workflowStatus);

    overview.total += cap;

    if (status === "COMPLETED") overview.completed += cap;
    else if (status === "CONSTRUCTION") overview.underConstruction += cap;
    else if (status === "LOA") overview.operational += cap;
    else overview.planning += cap;
  });

  return overview;
}

function buildRecentProjects(projects, count = 8) {
  return [...projects]
    .sort((a, b) => {
      const ad = a.createdAt?.seconds || a.updatedAt?.seconds || 0;
      const bd = b.createdAt?.seconds || b.updatedAt?.seconds || 0;
      return bd - ad;
    })
    .slice(0, count)
    .map((p) => ({
      id: p.id,
      projectName:
        p.projectName ||
        p.applicationId ||
        p.societyName ||
        p.pacsName ||
        "Godown Project",
      state: getProjectState(p),
      district: getProjectDistrict(p),
      pacs: p.societyName || p.pacsName || p.applicantName || "-",
      capacityMt: getProjectCapacity(p),
      status: normalizeStatus(p.status || p.projectStatus || p.workflowStatus),
      rawStatus: p.status || p.projectStatus || p.workflowStatus || "APPLIED",
    }));
}

function buildNotifications({ users = [], projects = [], mails = [] }) {
  const items = [];

  const pendingUsers = users.filter(
    (u) =>
      u.status === "PENDING_SHINE_AGRI" ||
      u.status === "PENDING_ROLE_APPROVAL" ||
      u.shineAgriVerificationStatus === "PENDING"
  );

  if (pendingUsers.length) {
    items.push({
      type: "USER_APPROVAL",
      title: `${pendingUsers.length} user registrations pending`,
      subtitle: "Shine Agri verification / role approval required",
      icon: "👤",
      priority: "HIGH",
    });
  }

  const dprPending = projects.filter((p) => normalizeStatus(p.status) === "DPR").length;
  if (dprPending) {
    items.push({
      type: "DPR",
      title: `DPR pending for ${dprPending} projects`,
      subtitle: "Please review DPR workflow",
      icon: "📄",
      priority: "MEDIUM",
    });
  }

  const tenderPending = projects.filter((p) => normalizeStatus(p.status) === "TENDER").length;
  if (tenderPending) {
    items.push({
      type: "TENDER",
      title: `Tender process pending for ${tenderPending} projects`,
      subtitle: "Tender approval / publication required",
      icon: "⚠️",
      priority: "MEDIUM",
    });
  }

  const pendingMails = mails.filter((m) => (m.status || "PENDING") === "PENDING").length;
  if (pendingMails) {
    items.push({
      type: "MAIL",
      title: `${pendingMails} mail notifications pending`,
      subtitle: "Notification queue requires processing",
      icon: "✉️",
      priority: "LOW",
    });
  }

  return items.slice(0, 8);
}

async function readCollection(name) {
  const snap = await getDocs(collection(db, name));
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

export async function getNafedDashboardData(scope = {}) {
  const [
    allProjects,
    users,
    branches,
    pmcs,
    allocations,
    mails,
  ] = await Promise.all([
    readCollection(PROJECT_COLLECTION),
    readCollection(USERS_COLLECTION),
    readCollection(BRANCH_COLLECTION),
    readCollection(PMC_COLLECTION),
    readCollection(ALLOCATION_COLLECTION),
    readCollection(MAIL_COLLECTION).catch(() => []),
  ]);

  const projects = applyScope(allProjects, scope);

  const pacsSet = new Set();
  const stateSet = new Set();
  const districtSet = new Set();

  let totalCapacity = 0;
  let totalInvestment = 0;

  projects.forEach((p) => {
    const pacsKey = getPacsKey(p);
    if (pacsKey) pacsSet.add(pacsKey);

    const state = getProjectState(p);
    if (state) stateSet.add(state);

    const district = getProjectDistrict(p);
    if (district) districtSet.add(district);

    totalCapacity += getProjectCapacity(p);
    totalInvestment += getProjectCost(p);
  });

  const statusOverview = buildStatusOverview(projects);
  const capacityOverview = buildCapacityOverview(projects);
  const stateOverview = buildStateOverview(projects);
  const recentProjects = buildRecentProjects(projects);
  const notifications = buildNotifications({ users, projects, mails });

  return {
    projects,
    users,
    branches,
    pmcs,
    allocations,
    mails,

    kpis: {
      totalProjects: projects.length,
      totalCapacity,
      statesCovered: stateSet.size,
      districtsCovered: districtSet.size,
      pacsInvolved: pacsSet.size,
      branches: branches.length,
      pmcs: pmcs.length,
      totalInvestment,
    },

    statusOverview,
    capacityOverview,
    stateOverview,
    recentProjects,
    notifications,
  };
}

export function formatMetricNumber(value) {
  const n = Number(value || 0);

  if (n >= 10000000) return `${(n / 10000000).toFixed(2)} Cr`;
  if (n >= 100000) return `${(n / 100000).toFixed(2)} L`;
  return n.toLocaleString("en-IN");
}

export function formatCapacity(value) {
  const n = Number(value || 0);

  if (n >= 10000000) return `${(n / 10000000).toFixed(2)} Cr MT`;
  if (n >= 100000) return `${(n / 100000).toFixed(2)} LMT`;
  return `${n.toLocaleString("en-IN")} MT`;
}

export function formatCurrency(value) {
  const n = Number(value || 0);

  if (n >= 10000000) return `₹${(n / 10000000).toFixed(2)} Cr`;
  if (n >= 100000) return `₹${(n / 100000).toFixed(2)} L`;
  return `₹${n.toLocaleString("en-IN")}`;
}